import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/primitives";
import AssessmentCTA from "@/components/sections/AssessmentCTA";
import { INSIGHTS, IMAGES } from "@/data/content";

const COVERS = [
    IMAGES.aerialContainers,
    IMAGES.portDusk,
    IMAGES.craneDusk,
    IMAGES.shipDocked,
];

export default function Insights() {
    const [feature, ...rest] = INSIGHTS;
    return (
        <>
            <PageHeader
                eyebrow="Insights"
                titleLines={["Perspectives on", "operational automation"]}
                description="Practical thinking on efficiency, manual work and the future of operations in shipping and logistics."
            />

            <section className="py-20 md:py-24">
                <div className="mx-auto max-w-7xl px-6 lg:px-10">
                    {/* Featured */}
                    <Reveal>
                        <a
                            href="#"
                            data-testid="insight-featured"
                            className="group grid overflow-hidden rounded-3xl border border-border bg-card transition-colors duration-300 hover:border-foreground/25 lg:grid-cols-2"
                        >
                            <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto">
                                <img
                                    src={COVERS[0]}
                                    alt={feature.title}
                                    loading="lazy"
                                    className="h-full w-full object-cover grayscale transition-all duration-700 ease-premium group-hover:scale-[1.03] group-hover:grayscale-0"
                                />
                            </div>
                            <div className="flex flex-col justify-between p-8 md:p-12">
                                <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                    <span className="text-navy">
                                        {feature.category}
                                    </span>
                                    <span>·</span>
                                    <span>{feature.date}</span>
                                    <span>·</span>
                                    <span>{feature.readTime}</span>
                                </div>
                                <div className="mt-6">
                                    <h2 className="text-3xl font-bold leading-tight tracking-tight md:text-4xl">
                                        {feature.title}
                                    </h2>
                                    <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">
                                        {feature.excerpt}
                                    </p>
                                </div>
                                <span className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                                    Read article
                                    <ArrowUpRight
                                        className="h-4 w-4 text-navy transition-transform duration-300 ease-premium group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                        strokeWidth={2}
                                    />
                                </span>
                            </div>
                        </a>
                    </Reveal>

                    {/* Grid */}
                    <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                        {rest.map((a, i) => (
                            <Reveal key={a.title} delay={i * 0.08}>
                                <motion.a
                                    href="#"
                                    data-testid={`insight-card-${i}`}
                                    whileHover={{ y: -6 }}
                                    transition={{
                                        duration: 0.4,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card transition-colors duration-300 hover:border-foreground/25"
                                >
                                    <div className="relative aspect-[16/10] overflow-hidden">
                                        <img
                                            src={COVERS[i + 1]}
                                            alt={a.title}
                                            loading="lazy"
                                            className="h-full w-full object-cover grayscale transition-all duration-700 ease-premium group-hover:scale-[1.04] group-hover:grayscale-0"
                                        />
                                    </div>
                                    <div className="flex flex-1 flex-col p-7">
                                        <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                            <span className="text-navy">
                                                {a.category}
                                            </span>
                                            <span>·</span>
                                            <span>{a.date}</span>
                                        </div>
                                        <h3 className="mt-4 text-xl font-semibold leading-snug tracking-tight">
                                            {a.title}
                                        </h3>
                                        <p className="mt-3 flex-1 text-[15px] leading-relaxed text-muted-foreground">
                                            {a.excerpt}
                                        </p>
                                        <span className="mt-6 font-mono text-xs uppercase tracking-[0.16em] text-muted-foreground">
                                            {a.readTime}
                                        </span>
                                    </div>
                                </motion.a>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <AssessmentCTA />
        </>
    );
}
